import {
  chakra,
  Input,
  InputLeftElement,
  InputProps,
  InputRightElement,
  Spinner,
} from "@chakra-ui/react";
import { RiPlayListAddLine } from "@remixicon/react";
import { forwardRef } from "react";

import BuListCheck from "@/common/assets/play-list-check-line.svg?react";
import { fieldIcons } from "@/common/components/AttributeFilters";
import AutocompleteInput from "@/common/components/AutocompleteInput";
import { AttributeField } from "@reflag/shared/attributeFilter";

const ListCheckIcon = chakra(BuListCheck);
const ListAddIcon = chakra(RiPlayListAddLine);

type RuleAttributeProps = Omit<InputProps, "value" | "onChange"> & {
  value: AttributeField["key"] | undefined;
  attributes: AttributeField[];
  isLoading?: boolean;
  onChange: (nextValue: AttributeField["key"]) => void;
};

const RuleAttribute = forwardRef<HTMLInputElement, RuleAttributeProps>(
  ({ value, attributes, isLoading, onChange, ...rest }, ref) => {
    const isTracked = attributes.some((attr) => attr.key === value);
    const attribute = attributes.find((attr) => attr.key === value);

    return (
      <AutocompleteInput
        maxSuggestions={50}
        search={value ?? ""}
        suggestions={attributes.map((a) => ({
          key: a.key,
          label: a.label || a.key,
          icon: fieldIcons[a.icon ?? "custom"],
        }))}
        onValuePick={(pick) => onChange(pick.key)}
      >
        <InputLeftElement color="gray.500" pl="2" pointerEvents="none">
          {isLoading ? (
            <Spinner size="xs" variant="subtle" />
          ) : (
            fieldIcons[attribute?.icon ?? "custom"]
          )}
        </InputLeftElement>
        <Input
          ref={ref}
          autoComplete="off"
          background="appBackground"
          data-testid="rule-attribute"
          htmlSize={1}
          placeholder={isLoading ? "Loading attributes..." : "Attribute"}
          type="search"
          value={attribute?.label ?? value ?? ""}
          onChange={(e) => {
            onChange(attribute?.system ? "" : e.target.value);
          }}
          {...rest}
        />
        <InputRightElement h="100%">
          {value?.trim() ? (
            isTracked ? (
              <ListCheckIcon boxSize={4} color="veryDimmed" />
            ) : (
              <ListAddIcon boxSize={4} color="veryDimmed" />
            )
          ) : null}
        </InputRightElement>
      </AutocompleteInput>
    );
  },
);

RuleAttribute.displayName = "RuleAttribute";

export default RuleAttribute;
